/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, BookOpen, ArrowLeft } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { HELP_CATEGORIES, labelFor } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function PortalHelp() {
    const [articles, setArticles] = useState([]);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [active, setActive] = useState(null);

    async function load() {
        try {
            const params = {};
            if (category !== "all") params.category = category;
            if (search) params.search = search;
            const { data } = await api.get("/help/", { params });
            setArticles(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { load(); /* eslint-disable-line */ }, [search, category]);

    if (active) {
        return (
            <div>
                <button onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]" data-testid="help-back">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to help
                </button>
                <div className="mt-4 co-card co-card-pad">
                    <div className="co-label">{labelFor(HELP_CATEGORIES, active.category)}</div>
                    <h1 className="font-display text-3xl font-medium tracking-tight mt-2">{active.title}</h1>
                    <div className="prose prose-sm max-w-none mt-6 text-[#0F1115] whitespace-pre-line">{active.body}</div>
                    {active.video_url && (
                        <a href={active.video_url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm text-blue-700 underline underline-offset-4">
                            Watch the video walkthrough
                        </a>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="co-label">Help Centre</div>
            <h1 className="font-display text-3xl tracking-tight font-medium mt-1">Quick answers</h1>
            <p className="text-sm text-[#5E636E] mt-2">Step-by-step guides for the common stuff. Search before you ask.</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_220px]">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E636E]" />
                    <Input className="pl-9" placeholder="Search 'authenticator', 'stripe', 'bank statement'…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="portal-help-search" />
                </div>
                <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="portal-help-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{HELP_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>

            <div className="mt-6">
                {articles.length === 0 ? (
                    <EmptyState icon={BookOpen} title="No articles match" description="Try a different search term or category." />
                ) : (
                    <ul className="space-y-2" data-testid="portal-help-list">
                        {articles.map((a) => (
                            <li key={a.id}>
                                <button
                                    onClick={() => setActive(a)}
                                    className="w-full text-left co-card co-card-pad hover:shadow-sm transition-shadow"
                                    data-testid={`portal-help-${a.slug}`}
                                >
                                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">{labelFor(HELP_CATEGORIES, a.category)}</div>
                                    <div className="mt-1 font-display text-lg font-medium">{a.title}</div>
                                    <div className="mt-2 text-xs text-[#5E636E] line-clamp-2 whitespace-pre-line">{a.body}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
